import { useMemo, useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from '@tanstack/react-table';

import { TableFilterDropdown } from './table-filter-dropdown';
import { Slider } from '@/components/ui/slider';
import { Button, Typography } from '@/components';
import { OpportunityItemList } from '@/features/opportunities/types';
import { getCapacityFromOpportunity } from '@/features/opportunities/helpers';
import { niceMax } from '@/utils/number';
import { Input } from '@/components/ui/input';

type Range = [number, number];

export type CapacityFilterProps = {
  table: Table<OpportunityItemList>;
  value?: string[];
  onChange: (value: string[]) => void;
  capMin?: number;
  debounceMs?: number;
  datasetMaxOverride?: number;
  popoverFilter?: boolean;
};

const inputClasses =
  'text-base-primary hover:border-base-primary focus:shadow-primary focus:border-primary-700 focus-visible:border-primary-700 active:border-primary-700 focus:ring-primary-50 h-6 border border-neutral-50 font-normal';

const parseToken = (token?: string): Range | null => {
  if (!token) return null;
  if (token.trim() === '>50') return [50, Number.POSITIVE_INFINITY];
  const [a, b] = token.split('-').map((s) => s.trim());
  const min = Number(a);
  const max = Number(b);
  if (Number.isNaN(min) || Number.isNaN(max)) return null;
  return [min, max];
};
const toToken = ([min, max]: Range) => `${min}-${max}`;

const formatAbbr = (n: number) => {
  if (n >= 1_000_000) return `${Math.round(n / 100_000) / 10}M`;
  if (n >= 1_000) return `${Math.round(n / 100) / 10}K`;
  return `${n}`;
};
const formatMW = (n: number) => `${formatAbbr(n)} MW`;

const badgeFromValues = (vals: string[]) => {
  const token = vals[0];
  if (!token) return null;
  const [a, b] = token.split('-').map((s) => s.trim());
  const min = Number(a),
    max = Number(b);
  if (Number.isNaN(min) || Number.isNaN(max)) return null;
  return `${formatAbbr(min)} - ${formatAbbr(max)}`;
};

export function CapacityFilter({
  table,
  value = [],
  onChange,
  capMin = 0,
  debounceMs = 300,
  datasetMaxOverride,
  popoverFilter
}: CapacityFilterProps) {
  const { t } = useTranslation(['home']);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 1) Maximum of the dataset (override if it comes from the parent)
  const datasetMax = useMemo(() => {
    if (typeof datasetMaxOverride === 'number') return datasetMaxOverride;
    const rows = table.getPreFilteredRowModel()?.flatRows ?? [];
    if (!rows.length) return 0;
    let max = 0;
    for (const r of rows) {
      const cap = Number(getCapacityFromOpportunity(r.original)) || 0;
      if (cap > max) max = cap;
    }
    return max;
  }, [datasetMaxOverride, table]);

  const computedCapMax = useMemo(() => niceMax(datasetMax, { targetTicks: 6, min: 50, paddingPct: 0.1 }), [datasetMax]);

  // 2) Initial state (from URL or full)
  const initial: Range = useMemo(() => {
    const parsed = parseToken(value[0]);
    if (parsed) {
      const min = Math.max(capMin, parsed[0]);
      const rawMax = parsed[1] === Number.POSITIVE_INFINITY ? computedCapMax : parsed[1];
      const max = Math.min(computedCapMax, rawMax);
      return min <= max ? [min, max] : [max, min];
    }
    return [capMin, computedCapMax];
  }, [value, capMin, computedCapMax]);

  const [range, setRange] = useState<Range>(initial);

  // Controlled inputs (such as string to allow erasing/writing)
  const [minInput, setMinInput] = useState<string>(String(initial[0]));
  const [maxInput, setMaxInput] = useState<string>(String(initial[1]));

  useEffect(() => {
    setRange(initial);
    setMinInput(String(initial[0]));
    setMaxInput(String(initial[1]));
  }, [initial]);

  // Clamp if dynamic capMax changes (without re-injecting into URL if it was not active)
  const isActive = (value?.filter(Boolean).length ?? 0) > 0;
  useEffect(() => {
    setRange((prev) => {
      const min = Math.max(capMin, Math.min(prev[0], computedCapMax));
      const max = Math.max(min, Math.min(prev[1], computedCapMax));
      setMinInput(String(min));
      setMaxInput(String(max));
      if ((min !== prev[0] || max !== prev[1]) && isActive) {
        onChange([toToken([min, max])]);
      }
      return [min, max];
    });
  }, [computedCapMax, capMin, isActive, onChange]);

  const debouncedEmit = (next: Range) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onChange([toToken(next)]);
    }, debounceMs);
  };

  // 3) Slider
  const onSliderChange = (vals: number[]) => {
    const nextMin = Math.max(capMin, vals[0]);
    const nextMax = Math.min(computedCapMax, vals[1]);
    const next: Range = nextMin <= nextMax ? [nextMin, nextMax] : [nextMax, nextMin];
    setRange(next);
    setMinInput(String(next[0]));
    setMaxInput(String(next[1]));
    debouncedEmit(next);
  };

  // 4) Manual inputs
  const commitInputs = () => {
    const parsedMin = Number(minInput);
    const parsedMax = Number(maxInput);
    if (Number.isNaN(parsedMin) || Number.isNaN(parsedMax)) {
      // Restores current valid values
      setMinInput(String(range[0]));
      setMaxInput(String(range[1]));
      return;
    }
    let min = Math.max(capMin, Math.min(parsedMin, computedCapMax));
    let max = Math.max(capMin, Math.min(parsedMax, computedCapMax));
    if (min > max) [min, max] = [max, min];
    const next: Range = [min, max];
    setRange(next);
    setMinInput(String(min));
    setMaxInput(String(max));
    debouncedEmit(next);
  };

  const clear = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const next: Range = [capMin, computedCapMax];
    setRange(next);
    setMinInput(String(next[0]));
    setMaxInput(String(next[1]));
    onChange([]); // remove url paramenter
  };

  // 5) Resume
  const selectedText = `${formatMW(range[0])} - ${formatMW(range[1])}`;
  const delta = range[1] - range[0];

  return (
    <TableFilterDropdown
      column={table.getColumn('capacity')!}
      title={t('Capacity')}
      onClear={clear}
      getBadgeLabel={badgeFromValues}
      value={value}
      popoverFilter={popoverFilter}
    >
      <div className="flex flex-col gap-3 p-1">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Typography size="caption" color="secondary">
              {t('Minimum')}
            </Typography>
            <Input
              inputMode="numeric"
              value={minInput}
              onChange={(e) => setMinInput(e.target.value.replace(/[^\d]/g, ''))}
              onBlur={commitInputs}
              onKeyDown={(e) => e.key === 'Enter' && commitInputs()}
              className={inputClasses}
            />
          </div>
          <div>
            <Typography size="caption" color="secondary">
              {t('Maximum')}
            </Typography>
            <Input
              inputMode="numeric"
              value={maxInput}
              onChange={(e) => setMaxInput(e.target.value.replace(/[^\d]/g, ''))}
              onBlur={commitInputs}
              onKeyDown={(e) => e.key === 'Enter' && commitInputs()}
              className={inputClasses}
            />
          </div>
        </div>

        <div className="flex items-center justify-between px-1 text-[11px] text-neutral-500">
          <span>{formatMW(capMin)}</span>
          <span>{formatMW(computedCapMax)}</span>
        </div>

        <Slider
          value={[range[0], range[1]]}
          min={capMin}
          max={computedCapMax}
          step={1}
          onValueChange={onSliderChange}
        />

        <div className="bg-neutral-25 rounded-md border border-neutral-50 px-3 py-1">
          <Typography color="primary" className="text-muted-foreground text-xs">
            {t('Current selection')}
          </Typography>
          <Typography as="p" size="caption" weight="medium" className="text-foreground text-sm">
            {selectedText}
          </Typography>
          <Typography as="p" size="caption" color="secondary" className="mt-1">
            {t('Range')}: {formatMW(delta)}
          </Typography>
        </div>

        <div className="flex justify-end">
          <Button size="sm" color="neutral" onClick={clear}>
            {t('Clear')}
          </Button>
        </div>
      </div>
    </TableFilterDropdown>
  );
}
